import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet-async';
import { getGeoLocation, handleFormSubmission } from '../services/apiService';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    country_code: '+1',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [detectedCountry, setDetectedCountry] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Fetch geolocation data
    const fetchGeoLocation = async () => {
      const geoData = await getGeoLocation();
      if (geoData && geoData.isSuccess) {
        setDetectedCountry(geoData.data.countryName);
      }
    };
    
    fetchGeoLocation();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await handleFormSubmission(formData);
      
      if (result.success) {
        toast.success(result.message || 'Thank you! Redirecting...');
        setFormData({ name: '', email: '', country_code: '+1', phone: '', subject: '', message: '' });
        
        // Redirect to application page with customer ID
        setTimeout(() => {
          window.location.href = result.redirectUrl;
        }, 1000);
      } else {
        toast.error(result.message || 'Submission failed. Please try again.');
      }
    } catch (error) {
      toast.error('An unexpected error occurred. Please try again.');
      console.error('Contact form error:', error);
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: <Mail className="h-6 w-6" />,
      title: 'Email Us',
      content: 'info@trademarkvenus.com',
      link: 'mailto:info@trademarkvenus.com',
    },
    {
      icon: <Phone className="h-6 w-6" />,
      title: 'Call Us',
      content: '+1 (888) 271-4168',
      link: 'tel:1-800-862-7263',
    },
    {
      icon: <MapPin className="h-6 w-6" />,
      title: 'Visit Us',
      content: 'United States',
      link: null,
    },
  ];

  return (
    <div className="min-h-screen" data-testid="contact-page">
      <Helmet>
        <title>Contact Us - Trademark Venus | Get Expert Trademark Help Today</title>
        <meta name="description" content="Contact Trademark Venus for trademark registration questions. Call +1 (888) 271-4168 or email info@trademarkvenus.com. Fast response within 24 hours. Located in Washington, DC." />
      </Helmet>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/8112131/pexels-photo-8112131.jpeg"
            alt="Hero background"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-red-900 opacity-90" />
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-sm font-medium tracking-widest uppercase text-red-400 mb-4 block">
              Get In Touch
            </span>
            <h1 className="text-5xl md:text-6xl font-heading font-bold text-white leading-tight mb-6">
              Contact Us
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed">
              Have questions about our services? We're here to help. Reach out to our team and we'll get back to you as soon as possible.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-6 border border-slate-100 text-center"
              >
                <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl w-14 h-14 flex items-center justify-center text-red-700 mx-auto mb-4">
                  {info.icon}
                </div>
                <h3 className="font-heading font-bold text-lg text-slate-900 mb-2">
                  {info.title}
                </h3>
                {info.link ? (
                  <a href={info.link} className="text-slate-600 hover:text-red-700 transition-colors">
                    {info.content}
                  </a>
                ) : (
                  <p className="text-slate-600">{info.content}</p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-white to-slate-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-slate-100">
                <h2 className="text-3xl font-heading font-bold text-slate-900 mb-2">
                  Send Us a Message
                </h2>
                <p className="text-slate-600 mb-2">
                  Fill out the form below and we'll respond within 24 hours.
                </p>
                {detectedCountry && (
                  <p className="text-sm text-slate-600 mb-6">
                    📍 Detected Location: {detectedCountry}
                  </p>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="contact-name">Name</Label>
                    <Input
                      id="contact-name"
                      data-testid="contact-name-input"
                      placeholder="Enter your full name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="h-12"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contact-email">Email</Label>
                    <Input
                      id="contact-email"
                      data-testid="contact-email-input"
                      type="email"
                      placeholder="your.email@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="h-12"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contact-phone">Contact Number</Label>
                    <div className="flex gap-2">
                      <Select
                        value={formData.country_code}
                        onValueChange={(value) => setFormData({ ...formData, country_code: value })}
                      >
                        <SelectTrigger className="w-[120px] h-12" data-testid="contact-country-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="+1">+1 (US)</SelectItem>
                          <SelectItem value="+44">+44 (UK)</SelectItem>
                          <SelectItem value="+91">+91 (IN)</SelectItem>
                          <SelectItem value="+61">+61 (AU)</SelectItem>
                          <SelectItem value="+86">+86 (CN)</SelectItem>
                          <SelectItem value="+31">+31 (NL)</SelectItem>
                          <SelectItem value="+49">+49 (DE)</SelectItem>
                          <SelectItem value="+33">+33 (FR)</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        id="contact-phone"
                        data-testid="contact-phone-input"
                        placeholder="123-456-7890"
                        value={formData.phone}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, '');
                          if (value.length <= 11) {
                            setFormData({ ...formData, phone: value });
                          }
                        }}
                        required
                        className="flex-1 h-12"
                        maxLength={11}
                        pattern="[0-9]*"
                        inputMode="numeric"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contact-subject">Subject</Label>
                    <Input
                      id="contact-subject"
                      data-testid="contact-subject-input"
                      placeholder="What is this regarding?"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      required
                      className="h-12"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contact-message">Message</Label>
                    <Textarea
                      id="contact-message"
                      data-testid="contact-message-input"
                      placeholder="Tell us how we can help you..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={6}
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit-button"
                    className="w-full h-12 bg-gradient-to-r from-red-700 to-red-600 hover:from-red-800 hover:to-red-700 text-white font-semibold rounded-full shadow-lg transition-all duration-300"
                  >
                    {loading ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
